import { MapPin, Phone, Mail, Clock, Loader2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

/**
 * Landing Page Contact Section
 * 
 * Displays contact information and map
 */
export default function LandingContactSection() {
  const [isInView, setIsInView] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const contactInfo = [
    {
      icon: MapPin,
      label: 'ADDRESS:',
      value: 'KK 587A St, Rebero, Kigali, Rwanda',
    },
    {
      icon: Phone,
      label: 'HOTEL TELEPHONE:',
      value: '+250 791 874 212',
    },
    {
      icon: Mail,
      label: 'EMAIL:',
      value: 'info@ikorohouse.com',
    },
    {
      icon: MapPin,
      label: 'GENERAL MANAGER',
      value: 'Gaston Rurangirwa',
    },
    {
      icon: Clock,
      label: 'CHECK-IN / CHECK-OUT:',
      value: '14h00 / 11h00',
    },
  ];

  return (
    <div ref={sectionRef} className="w-full py-12 md:py-16 bg-white">
      {/* <div className="border-t-2 border-amber-800 mb-8"></div> */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Left Section - Contact Information with 3D zoom animations */}
          <div className="space-y-6">
            {contactInfo.map((contact, index) => {
              const IconComponent = contact.icon;
              return (
                <div 
                  key={index} 
                  className="flex items-start gap-4 transition-all duration-700 ease-out"
                  style={{
                    transitionDelay: `${index * 100}ms`,
                    opacity: isInView ? 1 : 0,
                    transform: isInView 
                      ? 'perspective(1000px) translateZ(0) translateX(0) scale(1)' 
                      : 'perspective(1000px) translateZ(-200px) translateX(-50px) scale(0.7)'
                  }}
                >
                  <IconComponent className="w-5 h-5 mt-1 flex-shrink-0 text-black" strokeWidth={1.5} />
                  <div className="flex-1">
                    <div className="font-semibold text-sm mb-1" style={{ color: '#2C2416' }}>
                      {contact.label}
                    </div>
                    <div className="text-base" style={{ color: '#2C2416' }}>
                      {contact.icon === Phone ? (
                        <a href={`tel:${contact.value.replace(/\s+/g, '')}`} className="hover:text-amber-600 transition-colors">
                          {contact.value}
                        </a>
                      ) : (
                        contact.value
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Section - Map with 3D zoom animation */}
          <div 
            className="w-full h-full min-h-[400px] relative transition-all duration-700 ease-out"
            style={{
              transitionDelay: '200ms',
              opacity: isInView ? 1 : 0,
              transform: isInView 
                ? 'perspective(1000px) translateZ(0) scale(1)' 
                : 'perspective(1000px) translateZ(-200px) scale(0.7)'
            }}
          >
            {!mapLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Loading map...</p>
                </div>
              </div>
            )}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3987.5207!2d30.0707936!3d-1.9884906!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x19dca7e12a445d53%3A0x393fd65388aa8e7e!2sIKORO%20HOUSE%20-%20Rebero!5e0!3m2!1sen!2srw!4v1737974400000!5m2!1sen!2srw"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ikoro House Rebero Location"
              onLoad={() => setMapLoaded(true)}
              onClick={() => window.open('https://maps.app.goo.gl/8qeBhwepRvpMcgwC9', '_blank')}
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
}

